import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Star, MessageSquare, Camera, CheckCircle, AlertCircle, Calendar, Eye, Target, Users } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getDateRange } from '../lib/google-ads-client';

interface VisibilityRanking {
  company_id: string;
  company_name: string;
  county: string;
  is_our_company: boolean;
  review_count: number;
  average_rating: number;
  gmb_posts_last_30_days: number;
  gmb_posts_last_7_days: number;
  response_rate: number;
  response_time_hours: number;
  profile_completeness: number;
  photo_count: number;
  visibility_score: number;
  county_rank: number;
}

interface GMBPost {
  id: string;
  company_id: string;
  company_name: string;
  county: string;
  is_our_company: boolean;
  post_type: string;
  content: string;
  post_date: string;
  views: number;
  clicks: number;
}

const COUNTY_COMPANIES = {
  'Pinellas': { our: 'Hales AC', id: 'halesac' },
  'Hillsborough': { our: 'Hales AC', id: 'halesac' },
  'Palm Beach': { our: 'Climate Control Services', id: 'myccs' },
  'Broward': { our: 'Abraham AC', id: 'abrahamac' }
};

export default function CompetitorHeatmapPage() {
  const [selectedCounty, setSelectedCounty] = useState<string>('Pinellas');
  const [rankings, setRankings] = useState<VisibilityRanking[]>([]);
  const [gmbPosts, setGmbPosts] = useState<GMBPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [auctionInsights, setAuctionInsights] = useState<any[]>([]);

  useEffect(() => {
    loadData();
    loadAuctionInsights();
  }, [selectedCounty]);

  async function loadData() {
    setLoading(true);
    try {
      // Load visibility rankings for the county
      const { data: rankData, error: rankError } = await supabase
        .from('competitor_visibility_rankings')
        .select('*')
        .eq('county', selectedCounty)
        .order('county_rank');

      if (rankError) throw rankError;
      setRankings(rankData || []);

      // Load GMB posts for the county (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: postData, error: postError } = await supabase
        .from('gmb_posts')
        .select('*')
        .eq('county', selectedCounty)
        .gte('post_date', thirtyDaysAgo.toISOString())
        .order('post_date', { ascending: false });

      if (postError) throw postError;
      setGmbPosts(postData || []);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadAuctionInsights() {
    try {
      const range = getDateRange(30);
      const { data } = await supabase
        .from('google_ads_auction_insights')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate);

      if (data) {
        // Aggregate by competitor domain
        const grouped = new Map<string, any[]>();
        data.forEach(row => {
          const domain = row.competitor_domain;
          if (!grouped.has(domain)) {
            grouped.set(domain, []);
          }
          grouped.get(domain)?.push(row);
        });

        const aggregated = Array.from(grouped.entries()).map(([domain, rows]) => ({
          domain,
          impressionShare: rows.reduce((sum, r) => sum + r.impression_share, 0) / rows.length,
          overlapRate: rows.reduce((sum, r) => sum + r.overlap_rate, 0) / rows.length,
          positionAboveRate: rows.reduce((sum, r) => sum + r.position_above_rate, 0) / rows.length,
          topOfPageRate: rows.reduce((sum, r) => sum + r.top_of_page_rate, 0) / rows.length,
          absoluteTopRate: rows.reduce((sum, r) => sum + r.absolute_top_rate, 0) / rows.length
        })).sort((a, b) => b.impressionShare - a.impressionShare);

        setAuctionInsights(aggregated);
      }
    } catch (error) {
      console.error('Error loading auction insights:', error);
    }
  }

  const ourCompany = rankings.find(r => r.is_our_company);
  const competitors = rankings.filter(r => !r.is_our_company);
  const ourPosts = gmbPosts.filter(p => p.is_our_company);
  const competitorPosts = gmbPosts.filter(p => !p.is_our_company);

  const getScoreBadgeColor = (score: number) => {
    if (score >= 900) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 800) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (score >= 700) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getRankMedal = (rank: number) => {
    if (rank === 1) return { emoji: '🥇', color: 'text-yellow-600' };
    if (rank === 2) return { emoji: '🥈', color: 'text-gray-500' };
    if (rank === 3) return { emoji: '🥉', color: 'text-amber-700' };
    return { emoji: `#${rank}`, color: 'text-gray-600' };
  };

  const getPostTypeColor = (type: string) => {
    const colors = {
      'offer': 'bg-green-100 text-green-800',
      'update': 'bg-blue-100 text-blue-800',
      'event': 'bg-purple-100 text-purple-800',
      'product': 'bg-orange-100 text-orange-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Competitive Intelligence Dashboard</h1>
        <p className="text-gray-600">
          Track your visibility rankings and GMB posting activity vs. competitors in each county
        </p>
      </div>

      {/* County Selector */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(COUNTY_COMPANIES).map(([county, info]) => (
          <button
            key={county}
            onClick={() => setSelectedCounty(county)}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedCounty === county
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold text-gray-900">{county}</div>
            <div className="text-sm text-gray-600 mt-1">{info.our}</div>
            {selectedCounty === county && ourCompany && (
              <div className="mt-2 text-xs font-medium text-blue-600">
                Rank #{ourCompany.county_rank} · {ourCompany.visibility_score} pts
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Visibility Rankings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <Trophy className="w-6 h-6 text-yellow-600" />
          <h2 className="text-xl font-bold text-gray-900">Visibility Rankings - {selectedCounty} County</h2>
        </div>

        <div className="space-y-4">
          {rankings.map((company) => {
            const medal = getRankMedal(company.county_rank);
            const scoreColor = getScoreBadgeColor(company.visibility_score);

            return (
              <div
                key={company.company_id}
                className={`border-2 rounded-lg p-5 ${
                  company.is_our_company
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl ${medal.color}`}>{medal.emoji}</span>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">
                        {company.company_name}
                        {company.is_our_company && (
                          <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
                            Our Company
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                          {company.average_rating} ({company.review_count} reviews)
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {company.gmb_posts_last_30_days} posts/30d
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-lg border-2 ${scoreColor} font-bold`}>
                    {company.visibility_score} pts
                  </div>
                </div>

                {/* Ranking Factors Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Reviews</div>
                    <div className="font-semibold">{company.review_count}</div>
                    <div className="text-xs text-gray-500">+{Math.min(company.review_count, 200)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Rating Quality</div>
                    <div className="font-semibold">{company.average_rating.toFixed(1)} ⭐</div>
                    <div className="text-xs text-gray-500">+{Math.floor(company.average_rating * 40)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Recent Posts</div>
                    <div className="font-semibold">{company.gmb_posts_last_30_days}</div>
                    <div className="text-xs text-gray-500">+{Math.min(company.gmb_posts_last_30_days * 10, 150)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Response Rate</div>
                    <div className="font-semibold">{company.response_rate.toFixed(0)}%</div>
                    <div className="text-xs text-gray-500">+{Math.floor(company.response_rate)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Photos</div>
                    <div className="font-semibold">{company.photo_count}</div>
                    <div className="text-xs text-gray-500">+{Math.min(Math.floor(company.photo_count / 2), 50)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Profile Complete</div>
                    <div className="font-semibold">{company.profile_completeness}%</div>
                    <div className="text-xs text-gray-500">+{Math.floor(company.profile_completeness * 3)} pts</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Avg Response</div>
                    <div className="font-semibold">{company.response_time_hours.toFixed(1)}h</div>
                    <div className="text-xs text-gray-500">{company.response_time_hours < 3 ? '✓ Fast' : 'Slow'}</div>
                  </div>
                  <div className="bg-white rounded p-3 border border-gray-200">
                    <div className="text-xs text-gray-500 mb-1">Activity (7d)</div>
                    <div className="font-semibold">{company.gmb_posts_last_7_days} posts</div>
                    <div className="text-xs text-gray-500">{company.gmb_posts_last_7_days >= 2 ? '✓ Active' : 'Low'}</div>
                  </div>
                </div>

                {/* Why This Rank */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Why this rank:</div>
                  <div className="text-xs text-gray-600 space-y-1">
                    {company.county_rank === 1 && (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Leading in overall visibility score with strong reviews, active posting, and complete profile</span>
                      </div>
                    )}
                    {company.review_count >= 500 && (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>High review volume ({company.review_count}) builds trust and authority</span>
                      </div>
                    )}
                    {company.gmb_posts_last_30_days >= 8 && (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Very active GMB posting ({company.gmb_posts_last_30_days}/month) signals freshness</span>
                      </div>
                    )}
                    {company.response_rate >= 95 && (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Excellent review response rate ({company.response_rate.toFixed(0)}%) shows customer engagement</span>
                      </div>
                    )}
                    {company.profile_completeness >= 95 && (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>Complete profile ({company.profile_completeness}%) maximizes discovery opportunities</span>
                      </div>
                    )}
                    {company.response_rate < 70 && (
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                        <span>Low response rate ({company.response_rate.toFixed(0)}%) hurts engagement signals</span>
                      </div>
                    )}
                    {company.gmb_posts_last_30_days < 3 && (
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-3 h-3 text-red-600 mt-0.5 flex-shrink-0" />
                        <span>Infrequent posting reduces visibility in local search results</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* GMB Posting Activity Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Our Company Posts */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Our GMB Posts</h3>
            </div>
            <div className="text-sm font-medium text-blue-600">
              {ourPosts.length} posts (30d)
            </div>
          </div>

          {ourPosts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No recent posts</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ourPosts.slice(0, 5).map((post) => (
                <div key={post.id} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPostTypeColor(post.post_type)}`}>
                      {post.post_type}
                    </span>
                    <span className="text-xs text-gray-500">{formatTimeAgo(post.post_date)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{post.content}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {post.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {post.clicks} clicks
                    </span>
                    <span className="text-gray-500">
                      CTR: {post.views > 0 ? ((post.clicks / post.views) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Competitor Posts */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-bold text-gray-900">Competitor Activity</h3>
            </div>
            <div className="text-sm font-medium text-gray-600">
              {competitorPosts.length} posts (30d)
            </div>
          </div>

          {competitorPosts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>No competitor posts tracked</p>
            </div>
          ) : (
            <div className="space-y-4">
              {competitorPosts.slice(0, 5).map((post) => (
                <div key={post.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-700">{post.company_name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${getPostTypeColor(post.post_type)}`}>
                        {post.post_type}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{formatTimeAgo(post.post_date)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{post.content}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {post.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {post.clicks} clicks
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Google Ads Auction Insights */}
      {auctionInsights.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <Target className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Paid Search Competition (Google Ads)</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Competitor Domain</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impression Share</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Overlap Rate</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Position Above</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Top of Page</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Absolute Top</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auctionInsights.map((comp, idx) => (
                  <tr key={idx} className={idx === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {idx < 3 && <Trophy className="w-4 h-4 text-yellow-600 mr-2" />}
                        <span className="text-sm font-medium text-gray-900">{comp.domain}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      <span className="font-bold">{comp.impressionShare.toFixed(1)}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {comp.overlapRate.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {comp.positionAboveRate.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {comp.topOfPageRate.toFixed(1)}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {comp.absoluteTopRate.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-blue-900">Competitive Analysis</span>
            </div>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Impression Share:</strong> Your visibility in search results vs competitors</li>
              <li>• <strong>Overlap Rate:</strong> How often you compete directly for the same searches</li>
              <li>• <strong>Position Above:</strong> Percentage of time competitors rank above you</li>
              <li>• <strong>Top of Page:</strong> How often ads appear at top of search results</li>
            </ul>
          </div>
        </div>
      )}

      {/* Action Items */}
      {ourCompany && ourCompany.county_rank > 1 && (
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="font-bold text-yellow-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Opportunities to Improve Your Ranking
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ourCompany.gmb_posts_last_30_days < competitors[0]?.gmb_posts_last_30_days && (
              <div className="bg-white rounded p-4">
                <div className="font-semibold text-gray-900 mb-2">Increase Posting Frequency</div>
                <div className="text-sm text-gray-600">
                  Top competitor posts {competitors[0]?.gmb_posts_last_30_days}x/month vs your {ourCompany.gmb_posts_last_30_days}x
                </div>
              </div>
            )}
            {ourCompany.response_rate < competitors[0]?.response_rate && (
              <div className="bg-white rounded p-4">
                <div className="font-semibold text-gray-900 mb-2">Improve Response Rate</div>
                <div className="text-sm text-gray-600">
                  Top competitor: {competitors[0]?.response_rate.toFixed(0)}% vs yours: {ourCompany.response_rate.toFixed(0)}%
                </div>
              </div>
            )}
            {ourCompany.photo_count < competitors[0]?.photo_count && (
              <div className="bg-white rounded p-4">
                <div className="font-semibold text-gray-900 mb-2">Add More Photos</div>
                <div className="text-sm text-gray-600">
                  Top competitor: {competitors[0]?.photo_count} photos vs yours: {ourCompany.photo_count}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
